import { useQuery } from '@tanstack/react-query';

import { publicApi } from '@/config/axios';
import type { PageDto } from '@/lib/pagination';
import type {
  PublicEncounter,
  PublicFlowsheet,
  PublicGeneratedDocument,
  PublicPregnancy,
  PublicShareMeta,
  PublicUploadedDocument,
} from '../types';

function base(token: string) {
  return `/api/v1/public/patient-shares/${token}`;
}

export function usePublicShareMeta(token: string) {
  return useQuery({
    queryKey: ['public-share', token, 'meta'],
    queryFn: async () => {
      const { data } = await publicApi.get<PublicShareMeta>(base(token));
      return data;
    },
    retry: false,
  });
}

export function usePublicShareEncounters(token: string, enabled: boolean) {
  return useQuery({
    queryKey: ['public-share', token, 'encounters'],
    queryFn: async () => {
      const { data } = await publicApi.get<PageDto<PublicEncounter>>(
        `${base(token)}/encounters`,
        { params: { limit: 100 } },
      );
      return data;
    },
    enabled,
    retry: false,
  });
}

export function usePublicShareGeneratedDocuments(token: string, enabled: boolean) {
  return useQuery({
    queryKey: ['public-share', token, 'generated-documents'],
    queryFn: async () => {
      const { data } = await publicApi.get<PageDto<PublicGeneratedDocument>>(
        `${base(token)}/generated-documents`,
        { params: { limit: 100 } },
      );
      return data;
    },
    enabled,
    retry: false,
  });
}

export function usePublicShareUploadedDocuments(token: string, enabled: boolean) {
  return useQuery({
    queryKey: ['public-share', token, 'uploaded-documents'],
    queryFn: async () => {
      const { data } = await publicApi.get<PageDto<PublicUploadedDocument>>(
        `${base(token)}/uploaded-documents`,
        { params: { limit: 100 } },
      );
      return data;
    },
    enabled,
    retry: false,
  });
}

export function usePublicSharePregnancies(token: string, enabled: boolean) {
  return useQuery({
    queryKey: ['public-share', token, 'pregnancies'],
    queryFn: async () => {
      const { data } = await publicApi.get<PublicPregnancy[]>(`${base(token)}/pregnancies`);
      return data;
    },
    enabled,
    retry: false,
  });
}

export function usePublicShareFlowsheet(token: string, enabled: boolean) {
  return useQuery({
    queryKey: ['public-share', token, 'flowsheet'],
    queryFn: async () => {
      const { data } = await publicApi.get<PublicFlowsheet>(`${base(token)}/flowsheet`);
      return data;
    },
    enabled,
    retry: false,
  });
}
